import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import React from 'react';
import SingleTimeChart from './SingleTimeChart';
import {
  RESOLUTION_TIME_BUCKETS,
  RESPONSE_TIME_BUCKETS,
  TimeDistributionData,
} from './timeDistributionUtils';

interface TimeDistributionChartProps {
  responseTimeData?: TimeDistributionData; // Now expects processed bucket data
  resolutionTimeData?: TimeDistributionData; // Now expects processed bucket data
  totalResponseCases?: number;
  totalResolutionCases?: number;
  isLoading?: boolean;
}

const TimeDistributionChart: React.FC<TimeDistributionChartProps> = ({
  responseTimeData = {},
  resolutionTimeData = {},
  totalResponseCases,
  totalResolutionCases,
  isLoading = false,
}) => {
  // Data is already processed by the backend, no need to process again
  const processedResponseData: TimeDistributionData = responseTimeData;
  const processedResolutionData: TimeDistributionData = resolutionTimeData;

  if (isLoading) {
    return (
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-200 dark:border-gray-700"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Response & Resolution Time Distribution
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Analyzing case handling time patterns across different time ranges
          </p>
        </div>

        {/* Loading skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[1, 2].map(i => (
            <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-2xl p-6 animate-pulse">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-lg" />
                <div>
                  <div className="w-32 h-5 bg-gray-200 dark:bg-gray-600 rounded mb-2" />
                  <div className="w-24 h-3 bg-gray-200 dark:bg-gray-600 rounded" />
                </div>
              </div>
              <div className="h-80 bg-gray-200 dark:bg-gray-600 rounded-lg" />
            </div>
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 dark:from-gray-800/50 dark:via-blue-900/20 dark:to-purple-900/20 rounded-3xl p-8 shadow-xl border border-white/20 dark:border-gray-700/50 backdrop-blur-sm"
      initial={{ opacity: 0, y: 40, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.2, type: 'spring', stiffness: 80 }}
      whileHover={{ y: -2, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
    >
      {/* Header */}
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
          Response & Resolution Time Distribution
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Analyzing case handling time patterns across different time ranges
        </p>
      </motion.div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* First Response Time Chart */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, type: 'spring', stiffness: 100 }}
        >
          <SingleTimeChart
            type="response"
            data={processedResponseData}
            buckets={RESPONSE_TIME_BUCKETS}
            title="First Response Time"
            icon={<ClockIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />}
          />
        </motion.div>

        {/* Resolution Time Chart */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, type: 'spring', stiffness: 100 }}
        >
          <SingleTimeChart
            type="resolution"
            data={processedResolutionData}
            buckets={RESOLUTION_TIME_BUCKETS}
            title="Resolution Time"
            icon={<CheckCircleIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
          />
        </motion.div>
      </div>

      {/* Enhanced Summary Stats */}
      <motion.div
        className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        {/* Total Response Cases */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 
                     shadow-lg hover:shadow-2xl transform transition-all duration-500 ease-out
                     hover:scale-[1.02] hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-600
                     bg-gradient-to-br from-white via-emerald-50/30 to-emerald-100/20 
                     dark:from-gray-800 dark:via-emerald-900/10 dark:to-emerald-900/5
                     backdrop-blur-sm relative overflow-hidden group text-center"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.7, ease: 'easeOut' }}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-emerald-50/10 to-emerald-100/20 dark:via-emerald-900/5 dark:to-emerald-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative z-10">
            <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-2">
              {Object.values(processedResponseData)
                .reduce((sum, bucket) => sum + bucket.count, 0)
                .toLocaleString()}
            </div>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Response Cases
            </div>
          </div>
        </motion.div>

        {/* Total Resolution Cases */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 
                     shadow-lg hover:shadow-2xl transform transition-all duration-500 ease-out
                     hover:scale-[1.02] hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-600
                     bg-gradient-to-br from-white via-blue-50/30 to-blue-100/20 
                     dark:from-gray-800 dark:via-blue-900/10 dark:to-blue-900/5
                     backdrop-blur-sm relative overflow-hidden group text-center"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.8, ease: 'easeOut' }}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-50/10 to-blue-100/20 dark:via-blue-900/5 dark:to-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative z-10">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              {Object.values(processedResolutionData)
                .reduce((sum, bucket) => sum + bucket.count, 0)
                .toLocaleString()}
            </div>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Resolution Cases
            </div>
          </div>
        </motion.div>

        {/* Fastest Response Bucket */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 
                     shadow-lg hover:shadow-2xl transform transition-all duration-500 ease-out
                     hover:scale-[1.02] hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-600
                     bg-gradient-to-br from-white via-green-50/30 to-green-100/20 
                     dark:from-gray-800 dark:via-green-900/10 dark:to-green-900/5
                     backdrop-blur-sm relative overflow-hidden group text-center"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.9, ease: 'easeOut' }}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-green-50/10 to-green-100/20 dark:via-green-900/5 dark:to-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative z-10">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
              {processedResponseData['0-1h']?.percentage.toFixed(0) || 0}%
            </div>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
              &lt; 1 Hour Response
            </div>
          </div>
        </motion.div>

        {/* Fastest Resolution Bucket */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 
                     shadow-lg hover:shadow-2xl transform transition-all duration-500 ease-out
                     hover:scale-[1.02] hover:-translate-y-1 hover:border-gray-300 dark:hover:border-gray-600
                     bg-gradient-to-br from-white via-cyan-50/30 to-cyan-100/20 
                     dark:from-gray-800 dark:via-cyan-900/10 dark:to-cyan-900/5
                     backdrop-blur-sm relative overflow-hidden group text-center"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 1.0, ease: 'easeOut' }}
          whileHover={{ scale: 1.02, y: -2, transition: { duration: 0.2 } }}
        >
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-cyan-50/10 to-cyan-100/20 dark:via-cyan-900/5 dark:to-cyan-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative z-10">
            <div className="text-3xl font-bold text-cyan-600 dark:text-cyan-400 mb-2">
              {processedResolutionData['0-6h']?.percentage.toFixed(0) || 0}%
            </div>
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
              &lt; 6 Hour Resolution
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default TimeDistributionChart;
